/**
 * Section catalog (P26).
 *
 * Lifted from `sample-wireframe/client/src/pages/Home.tsx` — same 15
 * sections, same vocabulary, same icons. Each section now carries a
 * `path` field so React Router can render it as a route.
 *
 * The route tree at `apps/web/src/App.tsx` walks this list to register
 * routes; the sidebar at `apps/web/src/components/console/Sidebar.tsx`
 * walks the same list to render nav items grouped by `group`.
 */
import {
  Activity,
  Boxes,
  Command,
  Fingerprint,
  Gauge,
  KeyRound,
  Layers,
  Network,
  PlugZap,
  Plus,
  Route,
  ServerCog,
  Settings,
  ShieldCheck,
  ShieldQuestion,
  SlidersHorizontal,
  UserCheck,
  Users,
  Webhook,
  type LucideIcon,
} from 'lucide-react';

export type SectionId =
  | 'dashboard'
  | 'catalog'
  | 'connectors'
  | 'composio-toolkits'
  | 'register'
  | 'sessions'
  | 'proxy'
  | 'triggers'
  | 'approvals'
  | 'policy'
  | 'audit'
  | 'usage'
  | 'runtime'
  | 'admin'
  | 'publisher'
  | 'access'
  | 'access-matrix'
  | 'admin-workgroups'
  | 'admin-clients'
  | 'admin-virtual-servers'
  | 'observability';

export type SectionGroup =
  | 'Command'
  | 'Discover'
  | 'Build'
  | 'Auth'
  | 'Automate'
  | 'Govern'
  | 'Operate'
  | 'Admin';

export interface Section {
  id: SectionId;
  path: string;
  label: string;
  eyebrow: string;
  group: SectionGroup;
  icon: LucideIcon;
  description: string;
  /**
   * When true, the sidebar skips this entry but the route still resolves.
   * Used to declutter the admin nav for sections that are scaffolds /
   * deferred (lifted from the original wireframe but not yet useful).
   * Re-enable by flipping the flag when the underlying feature ships.
   */
  hidden?: boolean;
}

export const SECTIONS: readonly Section[] = [
  {
    id: 'dashboard',
    path: '/',
    label: 'Command Center',
    eyebrow: 'Overview',
    group: 'Command',
    icon: Command,
    description: 'Executive posture, approvals, health, usage, and recent execution telemetry.',
  },
  {
    id: 'catalog',
    path: '/catalog',
    label: 'Tool Catalog',
    eyebrow: 'Discover',
    group: 'Discover',
    icon: Boxes,
    description:
      'Composio-style governed marketplace for approved toolkits, MCP servers, and tool actions.',
    // Hidden from the admin sidebar — admins use Publisher Workspace
    // (lifecycle + edit) for the same set of servers. The route still
    // resolves so when we ship the dedicated end-user UI it can render
    // here. Keep `My Access` visible since it's useful for admins to
    // verify their own effective permissions.
    hidden: true,
  },
  {
    id: 'connectors',
    path: '/connectors',
    label: 'Connector Catalog',
    eyebrow: 'Discover',
    group: 'Discover',
    icon: PlugZap,
    description:
      'Per-domain MCP connector URLs. Browse what your workgroups can access and copy the URL into Claude.ai.',
  },
  {
    id: 'register',
    path: '/build/register',
    label: 'Register Server',
    eyebrow: 'Build',
    group: 'Build',
    icon: Plus,
    description:
      'Multi-step intake for MCP servers, REST adapters, metadata, owners, and risk controls.',
  },
  {
    id: 'publisher',
    path: '/build/publisher',
    label: 'Publisher Workspace',
    eyebrow: 'Build',
    group: 'Build',
    icon: ServerCog,
    description: 'All servers your workgroups own. Click into any one for lifecycle controls.',
  },
  {
    id: 'sessions',
    path: '/build/sessions',
    label: 'Sessions',
    eyebrow: 'Build',
    group: 'Build',
    icon: Route,
    description:
      'Immutable execution contracts: user, connected account, enabled tools, MCP URL, and headers.',
    hidden: true,
  },
  {
    id: 'proxy',
    path: '/build/proxy',
    label: 'Proxy Runtime',
    eyebrow: 'Operate',
    group: 'Operate',
    icon: Network,
    description:
      'Live proxy /readyz + /metrics and the resolved backend-route table. Runtime telemetry, not authoring — sits next to Runtime Health.',
  },
  {
    id: 'triggers',
    path: '/automate/triggers',
    label: 'Triggers',
    eyebrow: 'Automate',
    group: 'Automate',
    icon: Webhook,
    description:
      'Webhook and polling subscriptions scoped to connected accounts and governed endpoints.',
    hidden: true,
  },
  {
    id: 'approvals',
    path: '/govern/approvals',
    label: 'Approvals',
    eyebrow: 'Govern',
    group: 'Govern',
    icon: ShieldQuestion,
    description: 'Risk-scored review queue for servers, scopes, triggers, and policy exceptions.',
  },
  {
    id: 'composio-toolkits',
    path: '/govern/composio-toolkits',
    label: 'SaaS App Allowlist',
    eyebrow: 'Govern',
    group: 'Govern',
    icon: ShieldCheck,
    description:
      'Which of Composio’s ~1,089 SaaS apps Stanford has approved, for which workgroups, and down to individual tools.',
  },
  {
    id: 'policy',
    path: '/govern/policy',
    label: 'Policy Studio',
    eyebrow: 'Govern',
    group: 'Govern',
    icon: SlidersHorizontal,
    description:
      'Rule builder, simulator, test cases, environment promotion, and exception controls.',
  },
  {
    id: 'audit',
    path: '/govern/audit',
    label: 'Audit Explorer',
    eyebrow: 'Govern',
    group: 'Govern',
    icon: Fingerprint,
    description:
      'Execution logs, session traces, payload redaction, SIEM export, and evidence bundles.',
  },
  {
    id: 'usage',
    path: '/operate/usage',
    label: 'Usage Analytics',
    eyebrow: 'Operate',
    group: 'Operate',
    icon: Activity,
    description: 'Usage summary and breakdowns by tool, toolkit, user, session, group, and client.',
  },
  {
    id: 'runtime',
    path: '/operate/runtime',
    label: 'Runtime Health',
    eyebrow: 'Operate',
    group: 'Operate',
    icon: ServerCog,
    description: 'Pods, queues, upstreams, kill switches, incidents, and runtime SLO posture.',
  },
  {
    id: 'admin',
    path: '/admin/settings',
    label: 'Admin Settings',
    eyebrow: 'Admin',
    group: 'Admin',
    icon: Settings,
    description:
      'Teams, roles, environments, API keys, clients, retention, notifications, and compliance.',
  },
  {
    id: 'access',
    path: '/access',
    label: 'My Access',
    eyebrow: 'Discover',
    group: 'Discover',
    icon: UserCheck,
    description:
      'Your effective workgroups, what you can call, and a wizard to request access to anything else.',
  },
  {
    id: 'access-matrix',
    path: '/admin/access',
    label: 'Access Matrix',
    eyebrow: 'Admin',
    group: 'Admin',
    icon: ShieldQuestion,
    description: 'Tool×WG matrix + per-user "view as" preview. Verify multi-user permissions.',
  },
  {
    id: 'admin-workgroups',
    path: '/admin/workgroups',
    label: 'Workgroups',
    eyebrow: 'Admin',
    group: 'Admin',
    icon: Users,
    description:
      'Stanford workgroups (WGs) — the unit of authorization. Create, edit, manage members, bulk-import via CSV.',
  },
  {
    id: 'admin-clients',
    path: '/admin/clients',
    label: 'OAuth Clients',
    eyebrow: 'Admin',
    group: 'Admin',
    icon: KeyRound,
    description:
      'OAuth client registrations (Claude Desktop, Cursor, S2S agents). Manage scopes, redirect URIs, and which virtual servers each client can call.',
  },
  {
    id: 'admin-virtual-servers',
    path: '/admin/virtual-servers',
    label: 'Virtual Servers',
    eyebrow: 'Admin',
    group: 'Admin',
    icon: Layers,
    description:
      'Curated bundles of tools served at /v/<slug>/mcp. Compose tools from multiple servers into a single endpoint per audience.',
  },
  {
    id: 'observability',
    path: '/operate/observability',
    label: 'Observability',
    eyebrow: 'Operate',
    group: 'Operate',
    icon: Gauge,
    description:
      'Deep links into Grafana, Prometheus, OTel collector, and the audit pipeline. The dashboards live in their own UIs; this page collects the entry points.',
  },
];

export const SECTION_GROUPS: readonly SectionGroup[] = [
  'Command',
  'Discover',
  'Build',
  'Auth',
  'Automate',
  'Govern',
  'Operate',
  'Admin',
];

/** Lookup helper used by route components to read their own metadata. */
export function findSection(id: SectionId): Section {
  const found = SECTIONS.find((s) => s.id === id);
  if (!found) throw new Error(`Unknown section "${id}"`);
  return found;
}
